/**
 * «Cuándo» en el asistente (antes el paso «Horario y baja», 26/10): con qué franja sale y cómo se dan de baja, además de
 * «Ahora» o «Programar» (que vive en `useConfirmarCampana`). Lo elegido vive en el borrador (`useBorradorCampana`) y el
 * horario que se manda, con el límite de Meta, en `useConfirmarCampana`; acá, lo que trae la plantilla, la confirmación
 * de la baja automática de Twilio y el guardado.
 *
 * - Lo que ya se sabe viene puesto (29/10/2026): la franja de la marca y la baja que trae la plantilla (`bajaSugerida`).
 *   Que venga puesto no es haberlo elegido: se guarda recién con «Siguiente», con lo que se ve en pantalla
 *   (`PATCH {horario, baja}`, que pone `horarioElegido: true` en la campaña). `horario: null` es la franja de la marca.
 * - La baja tiene que estar en la plantilla: la campaña no le agrega nada a lo que aprobó Meta. Una plantilla sin baja
 *   no sugiere nada: se elige a propósito.
 * - Con la plantilla en revisión se completa y se GUARDA igual (29/10/2026): el botón dice «Guardar», guarda el horario y
 *   la baja, y avisa que «Revisar y mandar» se abre cuando Meta la apruebe. Sólo «Revisar y mandar» espera a Meta. Antes
 *   el botón quedaba apagado y un F5 perdía lo elegido. El backend lo deja: `PATCH {horario, baja}` no mira el estado
 *   de la plantilla (que la baja esté en ella lo mira el chequeo).
 * - La plantilla es la GUARDADA en la campaña (`detalle`), que es la que sale: con otra elegida y sin guardar en «Qué
 *   les decís», la baja se elige con lo que trae la que va a salir.
 * - Saltar a otro paso con algo sin guardar (`cambios`) pregunta antes: «Guardar y seguir» es el «Siguiente» de acá, a
 *   ese paso (o al más lejano que deje lo guardado); «Descartar» vuelve a lo guardado.
 */

import { useMemo, useState } from "react";
import type { CampanaDetalle, ConfigCuenta, HorarioCampana, ModoTransporte, PlantillaDetalle } from "../../types";
import { oficialApi } from "../../api";
import type { PasoAsistente } from "../../logic/chequeo";
import { leerError, leerRespuesta, textoError } from "../../logic/respuesta";
import { mismoHorario, textoFranjas, type HorarioEnEdicion } from "../../logic/horario";
import { bajaDeEleccion, bajaDePlantilla, eleccionDeBaja, motivoBajaNoDisponible, type EleccionBaja } from "../../logic/baja";
import { hastaDonde, type Aviso, type CambiosDelPaso, type CtaPaso } from "../../logic/asistente";

export const TEXTO_GUARDADO_ESPERA_META = "Guardamos el horario y la baja. «Revisar y mandar» se abre cuando Meta apruebe la plantilla.";

export function useHorarioCampana({
  campana, eleccionBaja, horarioEd, guardado, detalle, plantillaAprobada, config, modo, recargar, cuando, horario, horarioIncompleto, guardando,
  registrarGuardado, descartar, setGuardando, setErrorPaso, setAvisoOk, irA, alGuardar,
}: {
  campana: CampanaDetalle | null;
  /** la baja guardada o elegida ("" = ninguna todavía: se muestra la que trae la plantilla) */
  eleccionBaja: EleccionBaja | "";
  /** el horario como se está editando y lo guardado (el horario y la baja), para saber si queda algo sin guardar */
  horarioEd: HorarioEnEdicion;
  guardado: { horario: HorarioEnEdicion; baja: EleccionBaja | "" };
  /** la plantilla guardada en la campaña (undefined mientras llega) */
  detalle: PlantillaDetalle | undefined;
  /** con la plantilla en revisión, «Revisar y mandar» espera la aprobación */
  plantillaAprobada: boolean;
  config: ConfigCuenta | null;
  modo: ModoTransporte | null;
  /** vuelve a pedir la cuenta (después de confirmar la baja automática) */
  recargar: () => Promise<void>;
  /** «Ahora» o «Programar» (null = sin elegir), si a «Programar» le falta la fecha y si la que tiene no se puede (`useConfirmarCampana`) */
  cuando: { modo: "ahora" | "programar" | null; fechaFaltante: boolean; fueraDeRango: string };
  /** el horario como se manda y si está mal cargado (`useConfirmarCampana`) */
  horario: HorarioCampana | null;
  horarioIncompleto: boolean;
  guardando: boolean;
  /** lo que devolvió el `PATCH` (`useBorradorCampana`): devuelve hasta qué paso deja llegar */
  registrarGuardado: (c: CampanaDetalle) => number;
  /** «Descartar»: el horario y la baja vuelven a lo guardado */
  descartar: () => void;
  setGuardando: (v: boolean) => void;
  setErrorPaso: (a: Aviso | null) => void;
  /** guardado con la plantilla en revisión: se queda en «Cuándo» y lo dice */
  setAvisoOk: (s: string) => void;
  irA: (p: PasoAsistente) => void;
  /** con otro horario, el chequeo de antes ya no vale */
  alGuardar: () => void;
}) {
  const [confirmandoOptOut, setConfirmandoOptOut] = useState(false);
  const [errorOptOut, setErrorOptOut] = useState("");

  const franjasMarcaTexto = config?.franjasTexto || textoFranjas(config?.franjas);
  // lo que trae la plantilla elegida: la baja que se elige tiene que estar ahí (la campaña no le agrega nada)
  const bajaDeLaPlantilla = useMemo(() => bajaDePlantilla(detalle, config?.botonBaja), [detalle, config?.botonBaja]);
  /** la que trae la plantilla, si trae alguna ("" sin la plantilla, o si no trae ni el botón ni BAJA) */
  const bajaSugerida = detalle && (bajaDeLaPlantilla.boton || bajaDeLaPlantilla.texto) ? eleccionDeBaja(bajaDeLaPlantilla) : "";
  /** la que se ve en pantalla y se guarda con «Siguiente» */
  const bajaEnPantalla: EleccionBaja | "" = eleccionBaja || bajaSugerida;
  const bajaNoDisponible = bajaEnPantalla && detalle ? motivoBajaNoDisponible(bajaEnPantalla, bajaDeLaPlantilla, config?.botonBaja) : "";
  // La palabra BAJA la reconoce también Twilio (Advanced Opt-Out) si el Messaging Service la tiene: se confirma acá,
  // cuando la campaña la ofrece, en vez de un aviso al confirmar.
  const pideOptOut = modo === "twilio" && !config?.advancedOptOutConfirmado && (bajaEnPantalla === "texto" || bajaEnPantalla === "los_dos");
  const confirmarOptOut = async () => {
    setConfirmandoOptOut(true);
    setErrorOptOut("");
    try {
      const l = leerRespuesta(await oficialApi.actualizarConfig({ advancedOptOutConfirmado: true }));
      if (!l.ok) { setErrorOptOut(l.error || textoError(l)); return; }
      await recargar();
    } catch (e) {
      setErrorOptOut(textoError(leerError(e)));
    } finally {
      setConfirmandoOptOut(false);
    }
  };
  /** Guarda lo que se ve y sigue a `destino` («Revisar y mandar», con «Siguiente»), o al más lejano que deje lo guardado. */
  const guardarHorario = async (destino: PasoAsistente = "revisar") => {
    if (!campana || !bajaEnPantalla || horarioIncompleto) return;
    setGuardando(true);
    setErrorPaso(null);
    try {
      const l = leerRespuesta<CampanaDetalle>(await oficialApi.editarCampana(campana.id, { horario, baja: bajaDeEleccion(bajaEnPantalla) }));
      if (!l.ok) { setErrorPaso({ texto: l.error || textoError(l) }); return; }
      const n = registrarGuardado(l.data);
      alGuardar();
      // «Revisar y mandar» todavía no se abre (Meta no aprobó la plantilla): queda guardado y se dice
      if (destino === "revisar" && !plantillaAprobada && hastaDonde(destino, n) !== destino) setAvisoOk(TEXTO_GUARDADO_ESPERA_META);
      else irA(hastaDonde(destino, n));
    } catch (e) {
      setErrorPaso({ texto: textoError(leerError(e)) });
    } finally {
      setGuardando(false);
    }
  };

  const motivo = cuando.modo === null ? "Elegí si sale ahora o la programás"
    : cuando.fechaFaltante ? "Elegí fecha y hora"
    : cuando.fueraDeRango ? cuando.fueraDeRango
    : horarioIncompleto ? "Revisá el horario de la campaña"
    : !bajaEnPantalla ? "Elegí cómo se dan de baja"
    : bajaNoDisponible ? bajaNoDisponible : "";
  // con la plantilla en revisión, guarda y se queda: «Revisar y mandar» espera a Meta
  const cta: CtaPaso = { motivo: motivo || null, onSiguiente: () => guardarHorario(), label: plantillaAprobada ? undefined : "Guardar", guardando };
  /** Lo de la pantalla no es lo guardado: otro horario u otra baja elegida a mano (la que trae la plantilla, puesta, no cuenta). */
  const cambios: CambiosDelPaso = {
    sinGuardar: !!campana && (!mismoHorario(horarioEd, guardado.horario) || eleccionBaja !== guardado.baja),
    noSeGuarda: horarioIncompleto ? "Revisá el horario de la campaña" : !bajaEnPantalla ? "Elegí cómo se dan de baja" : bajaNoDisponible || null,
    guardar: guardarHorario,
    descartar: (destino) => { descartar(); irA(destino); },
  };

  return { cta, cambios, franjasMarcaTexto, bajaDeLaPlantilla, bajaSugerida, bajaEnPantalla, pideOptOut, confirmarOptOut, confirmandoOptOut, errorOptOut };
}
